import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  Coins,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { useToast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { Field } from "@/components/ui/Field";
import { ScreenSpinner } from "@/components/ui/Spinner";
import { StatTile } from "@/components/ui/StatTile";
import type { Role } from "@/types";
import {
  AdminHeader,
  ROLE_LABEL,
  canAdjustBalance,
  canAssignRole,
  canManageUsers,
  errMessage,
} from "./shared";

type AdminUser = {
  id: string;
  name: string;
  nickname: string | null;
  email: string;
  role: Role;
  balance: number;
  creditBalance: number;
  createdAt: string;
  lastLogin: string | null;
  emailVerified: boolean;
  pixKey: string | null;
  betsCount: number;
  lastBetAt: string | null;
  totalStaked: number;
  totalWinnings: number;
};

const ROLES: Role[] = ["user", "collaborator", "admin", "super_admin"];

const ROLE_TONE: Record<Role, "success" | "accent" | "muted" | "destructive"> =
  {
    super_admin: "destructive",
    admin: "accent",
    collaborator: "success",
    user: "muted",
  };

const inputClass =
  "w-full rounded-(--radius-field) border border-input bg-card px-4 py-3.5 text-[15px] text-foreground outline-none transition-colors focus:border-tint";

export default function AdminUsuariosPage() {
  const { user: viewer, refreshUser } = useAuth();
  const { showToast } = useToast();

  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ users: AdminUser[] }>("/api/admin/users");
      setUsers(data.users);
      setError(null);
      // Mantém o contexto de auth em dia quando o admin altera a própria
      // conta (ex.: super admin ajusta o próprio saldo → Perfil reflete).
      await refreshUser();
    } catch (err) {
      setError(errMessage(err));
    }
  }, [refreshUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.nickname ?? "").toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [users, query]);

  const totals = useMemo(() => {
    const list = users ?? [];
    return {
      count: list.length,
      available: list.reduce((s, u) => s + u.balance, 0),
      credits: list.reduce((s, u) => s + u.creditBalance, 0),
    };
  }, [users]);

  if (!viewer) return null;

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Usuários"
        caption={`${totals.count} conta${totals.count === 1 ? "" : "s"} ativa${totals.count === 1 ? "" : "s"}`}
        action={
          canManageUsers(viewer.role) ? (
            <Button
              title="Novo"
              icon={<Plus size={16} />}
              variant="secondary"
              onClick={() => setShowCreate((v) => !v)}
              className="h-10 px-3 text-sm"
            />
          ) : undefined
        }
      />

      <div className="flex gap-3">
        <StatTile label="Saldo total" value={formatBRL(totals.available)} />
        <StatTile label="Créditos" value={formatBRL(totals.credits)} />
      </div>

      {canAdjustBalance(viewer.role) && (
        <BulkCreditCard
          users={users}
          onApplied={load}
          showToast={showToast}
        />
      )}

      {showCreate && (
        <CreateUserCard
          viewerRole={viewer.role}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}

      <div className="relative">
        <Search
          size={16}
          className="absolute top-1/2 left-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome, apelido ou e-mail"
          className={`${inputClass} pl-10`}
        />
      </div>

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!users && !error && <ScreenSpinner />}

      <div className="flex flex-col gap-3 pb-6">
        {filtered.map((u) => (
          <UserCard
            key={u.id}
            user={u}
            viewerId={viewer.id}
            viewerRole={viewer.role}
            expanded={expandedId === u.id}
            onToggle={() =>
              setExpandedId((cur) => (cur === u.id ? null : u.id))
            }
            onChanged={load}
            showToast={showToast}
          />
        ))}
        {users && filtered.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            Nenhum usuário encontrado.
          </Card>
        )}
      </div>
    </div>
  );
}

// Crédito em lote: um valor único creditado a todos os usuários ativos.
// Espelha POST /api/admin/users/credit-batch (planBulkCredit no api-server):
// só valores inteiros positivos, sempre em Saldo Créditos (jogável, nunca
// sacável).
type BulkAudience = "active" | "verified";

const BULK_AUDIENCE_OPTIONS: { value: BulkAudience; label: string }[] = [
  { value: "active", label: "Todos os usuários ativos" },
  { value: "verified", label: "Somente e-mail confirmado" },
];

function BulkCreditCard({
  users,
  onApplied,
  showToast,
}: {
  users: AdminUser[] | null;
  onApplied: () => Promise<void>;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [amountStr, setAmountStr] = useState("");
  const [audience, setAudience] = useState<BulkAudience>("active");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const amount = Number.parseInt(amountStr, 10);
  const valid = Number.isFinite(amount) && amount > 0;

  // Prévia local (o servidor é a fonte da verdade sobre quem recebe).
  const targetCount = (users ?? []).filter(
    (u) => audience === "active" || u.emailVerified,
  ).length;

  const apply = async () => {
    if (!valid || busy) return;
    if (
      !window.confirm(
        `Creditar ${formatBRL(amount)} para ${targetCount} usuário(s)? ` +
          `Total ${formatBRL(amount * targetCount)}.`,
      )
    )
      return;
    setBusy(true);
    try {
      const res = await apiFetch<{ affected: number; message: string }>(
        "/api/admin/users/credit-batch",
        {
          method: "POST",
          body: JSON.stringify({
            amount,
            audience,
            description: description.trim() || undefined,
          }),
        },
      );
      setAmountStr("");
      setDescription("");
      await onApplied();
      showToast({ type: "success", message: res.message });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 p-4 text-left"
      >
        <Coins size={16} className="shrink-0 text-tint" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">Crédito em lote</p>
          <p className="truncate text-xs text-muted-foreground">
            Um valor para todos os usuários ativos
          </p>
        </div>
        {open ? (
          <ChevronUp size={16} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={16} className="text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="flex flex-col gap-2 border-t border-border p-4">
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value as BulkAudience)}
            className={inputClass}
          >
            {BULK_AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <input
              value={amountStr}
              onChange={(e) =>
                setAmountStr(e.target.value.replace(/[^0-9]/g, ""))
              }
              inputMode="numeric"
              placeholder="Valor por usuário (R$)"
              className={inputClass}
            />
            <Button
              title="Creditar"
              loading={busy}
              disabled={!valid}
              onClick={() => void apply()}
              className="h-[46px] shrink-0 px-5 text-sm"
            />
          </div>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={120}
            placeholder="Descrição no extrato (opcional)"
            className={inputClass}
          />
          <p className="text-[11px] text-muted-foreground">
            {valid
              ? `${targetCount} usuário(s) receberão ${formatBRL(amount)} — total ${formatBRL(amount * targetCount)}.`
              : `${targetCount} usuário(s) no público selecionado.`}{" "}
            Créditos valem para apostar, mas nunca podem ser sacados.
          </p>
        </div>
      )}
    </Card>
  );
}

function UserCard({
  user: u,
  viewerId,
  viewerRole,
  expanded,
  onToggle,
  onChanged,
  showToast,
}: {
  user: AdminUser;
  viewerId: string;
  viewerRole: Role;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void>;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-bold">
            {u.name}
            {u.emailVerified && (
              <BadgeCheck size={14} className="shrink-0 text-tint" />
            )}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {u.nickname ? `@${u.nickname} · ` : ""}
            {u.email}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              {formatBRL(u.balance)}
            </span>{" "}
            disponível ·{" "}
            <span className="font-semibold text-foreground">
              {formatBRL(u.creditBalance)}
            </span>{" "}
            créditos · {u.betsCount} aposta{u.betsCount === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Chip tone={ROLE_TONE[u.role]}>{ROLE_LABEL[u.role]}</Chip>
          {expanded ? (
            <ChevronUp size={16} className="text-muted-foreground" />
          ) : (
            <ChevronDown size={16} className="text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="flex flex-col gap-4 border-t border-border p-4">
          <p className="text-[11px] text-muted-foreground">
            Criado em {formatDateTime(u.createdAt)}
            {u.lastLogin && ` · último acesso ${formatDateTime(u.lastLogin)}`}
            {u.lastBetAt && ` · última aposta ${formatDateTime(u.lastBetAt)}`}
            <br />
            Total apostado {formatBRL(u.totalStaked)} · prêmios{" "}
            {formatBRL(u.totalWinnings)}
            {u.pixKey && ` · PIX: ${u.pixKey}`}
          </p>

          {canAdjustBalance(viewerRole) && (
            <BalanceSection user={u} onChanged={onChanged} showToast={showToast} />
          )}

          {canManageUsers(viewerRole) && (
            <RoleSection
              user={u}
              viewerId={viewerId}
              viewerRole={viewerRole}
              onChanged={onChanged}
              showToast={showToast}
            />
          )}

          {viewerRole === "super_admin" && (
            <PasswordSection user={u} showToast={showToast} />
          )}

          {canManageUsers(viewerRole) && u.id !== viewerId && (
            <DeleteSection user={u} onChanged={onChanged} showToast={showToast} />
          )}
        </div>
      )}
    </Card>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-[10px] font-semibold tracking-[1.2px] text-secondary-foreground">
      {children.toUpperCase()}
    </p>
  );
}

// Regra de negócio (espelha planAdminAdjust do api-server): valores
// concedidos pelo admin nunca podem ser sacados — concessões entram como
// crédito; o saldo disponível (sacável) só cresce com prêmios de apostas ou
// estorno de saque. Ajuste no disponível aqui é só débito (correção).
type AdjustKind = "credit" | "debit" | "refund";

const ADJUST_OPTIONS: {
  value: AdjustKind;
  label: string;
  action: string;
  hint: string;
}[] = [
  {
    value: "credit",
    label: "Dar crédito (jogável)",
    action: "Creditar",
    hint: "Créditos valem para apostar, mas nunca podem ser sacados. Só prêmios de apostas viram saldo sacável.",
  },
  {
    value: "debit",
    label: "Debitar disponível (correção)",
    action: "Debitar",
    hint: "Remove valor do saldo disponível (sacável). Usado para correções.",
  },
  {
    value: "refund",
    label: "Estornar saque",
    action: "Estornar",
    hint: "Devolve ao saldo disponível um saque que foi debitado mas não pago (ex.: chave PIX inválida).",
  },
];

function BalanceSection({
  user: u,
  onChanged,
  showToast,
}: {
  user: AdminUser;
  onChanged: () => Promise<void>;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  const [amountStr, setAmountStr] = useState("");
  const [kind, setKind] = useState<AdjustKind>("credit");
  const [busy, setBusy] = useState(false);

  const amount = Number.parseInt(amountStr, 10);
  const valid = Number.isFinite(amount) && amount > 0;
  const option = ADJUST_OPTIONS.find((o) => o.value === kind)!;

  const adjust = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      // "debit" é o ajuste negativo no disponível; os demais são positivos.
      const payload =
        kind === "debit"
          ? { amount: -amount, balanceType: "available" }
          : { amount, balanceType: kind };
      await apiFetch(`/api/admin/users/${u.id}/balance`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setAmountStr("");
      await onChanged();
      showToast({ type: "success", message: "Ajuste aplicado." });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Ajustar saldo</SectionLabel>
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as AdjustKind)}
        className={inputClass}
      >
        {ADJUST_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <input
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9]/g, ""))}
          inputMode="numeric"
          placeholder="Valor (R$)"
          className={inputClass}
        />
        <Button
          title={option.action}
          variant={kind === "debit" ? "ghost" : "primary"}
          loading={busy}
          disabled={!valid}
          onClick={() => void adjust()}
          className="h-[46px] shrink-0 px-5 text-sm"
        />
      </div>
      <p className="text-[11px] text-muted-foreground">{option.hint}</p>
    </div>
  );
}

function RoleSection({
  user: u,
  viewerId,
  viewerRole,
  onChanged,
  showToast,
}: {
  user: AdminUser;
  viewerId: string;
  viewerRole: Role;
  onChanged: () => Promise<void>;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const isSelf = u.id === viewerId;
  const lockedTarget = u.role === "super_admin" && viewerRole !== "super_admin";

  const changeRole = async (role: Role) => {
    if (role === u.role || busy) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      });
      await onChanged();
      showToast({ type: "success", message: "Papel atualizado." });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Papel</SectionLabel>
      <select
        value={u.role}
        disabled={busy || isSelf || lockedTarget}
        onChange={(e) => void changeRole(e.target.value as Role)}
        className={`${inputClass} disabled:opacity-50`}
      >
        {ROLES.map((r) => (
          <option key={r} value={r} disabled={!canAssignRole(viewerRole, r)}>
            {ROLE_LABEL[r]}
          </option>
        ))}
      </select>
      {isSelf && (
        <p className="text-[11px] text-muted-foreground">
          Não é possível alterar o próprio papel.
        </p>
      )}
    </div>
  );
}

function PasswordSection({
  user: u,
  showToast,
}: {
  user: AdminUser;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = password.length >= 6;

  const reset = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/users/${u.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password }),
      });
      setPassword("");
      showToast({
        type: "success",
        message: "Senha redefinida. O usuário deverá trocá-la no próximo login.",
      });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Redefinir senha</SectionLabel>
      <div className="flex gap-2">
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="text"
          placeholder="Nova senha (mín. 6)"
          className={inputClass}
        />
        <Button
          title="Redefinir"
          variant="secondary"
          loading={busy}
          disabled={!valid}
          onClick={() => void reset()}
          className="h-[46px] shrink-0 px-4 text-sm"
        />
      </div>
    </div>
  );
}

function DeleteSection({
  user: u,
  onChanged,
  showToast,
}: {
  user: AdminUser;
  onChanged: () => Promise<void>;
  showToast: (t: { type: "success" | "error"; message: string }) => void;
}) {
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (busy) return;
    if (!window.confirm(`Excluir a conta de ${u.name} (${u.email})?`)) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/users/${u.id}`, { method: "DELETE" });
      await onChanged();
      showToast({ type: "success", message: "Usuário excluído." });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
      setBusy(false);
    }
  };

  return (
    <Button
      title="Excluir usuário"
      variant="ghost"
      icon={<Trash2 size={15} />}
      loading={busy}
      onClick={() => void remove()}
      className="h-10 border-destructive/50 text-sm text-[#ffb4ad] hover:bg-destructive/10"
    />
  );
}

function CreateUserCard({
  viewerRole,
  onCreated,
}: {
  viewerRole: Role;
  onCreated: () => void;
}) {
  const { showToast } = useToast();
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [busy, setBusy] = useState(false);

  const valid =
    name.trim().length >= 2 && email.includes("@") && password.length >= 6;

  const create = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await apiFetch("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          nickname: nickname.trim() || undefined,
          email: email.trim(),
          password,
          role,
        }),
      });
      showToast({ type: "success", message: "Usuário criado." });
      onCreated();
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
      setBusy(false);
    }
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <SectionLabel>Novo usuário</SectionLabel>
      <Field
        label="Nome"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nome completo"
      />
      <Field
        label="Apelido (opcional)"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        placeholder="Como aparece nos rankings"
      />
      <Field
        label="E-mail"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email@exemplo.com"
      />
      <Field
        label="Senha"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Mínimo 6 caracteres"
      />
      <label className="flex flex-col">
        <span className="mb-1.5 text-[10px] font-semibold tracking-[1.2px] text-secondary-foreground">
          PAPEL
        </span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className={inputClass}
        >
          {ROLES.filter((r) => canAssignRole(viewerRole, r)).map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
      </label>
      <Button
        title="Criar usuário"
        loading={busy}
        disabled={!valid}
        onClick={() => void create()}
      />
    </Card>
  );
}
